import React, { useState, useRef, useEffect, useCallback } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import confettiAnimation from "../assets/confetti.json";
import { useAuth } from "../contexts/AuthContext";
import { useGradient } from "../contexts/GradientContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useSearch } from "../contexts/SearchContext";
import { useTheme } from "../contexts/ThemeContext";
import apiService from "../services/api";
import { isReadOnlyPlaylistEntry } from "../utils/playlistDashboard";
import { fetchWeatherAndSetBackground } from "../utils/weatherBackground";

import { getEarnedBadges } from "./achievements/badgeDefinitions";
import OctagonBadge from "./achievements/OctagonBadge";
import ModuleAuditLogsModal from "./modals/ModuleAuditLogsModal";
import ResellerLogsModal from "./modals/ResellerLogsModal";
import NotificationDropdown from "./NotificationDropdown";
import PurchaseModal from "./PurchaseModal";
import SearchResults from "./SearchResults";
import Tooltip from "./Tooltip";

const shouldDismissBackdropOnKeyDown = (event) =>
  event.key === "Escape" ||
  ((event.key === "Enter" || event.key === " ") &&
    event.target === event.currentTarget);

const Layout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLandscape, setIsLandscape] = useState(
    window.innerHeight < window.innerWidth && window.innerWidth < 1024
  );
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [weatherCondition, setWeatherCondition] = useState("");
  const [temperature, setTemperature] = useState(null);
  const [weatherIcon, setWeatherIcon] = useState("");
  const [weatherType, setWeatherType] = useState("");
  const [isUSA, setIsUSA] = useState(false);
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);
  const [
    isImpersonationIndicatorDismissed,
    setIsImpersonationIndicatorDismissed,
  ] = useState(false);
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [showResellerLogsModal, setShowResellerLogsModal] = useState(false);
  const [showModuleAuditLogsModal, setShowModuleAuditLogsModal] =
    useState(false);
  const [earnedBadgesCount, setEarnedBadgesCount] = useState(0);
  const [newBadgesCount, setNewBadgesCount] = useState(0);
  const [showAchievementSplash, setShowAchievementSplash] = useState(false);
  const [newlyEarnedBadges, setNewlyEarnedBadges] = useState([]);
  const [showLowQuotaOverlay, setShowLowQuotaOverlay] = useState(false);
  const [userPlaylistCount, setUserPlaylistCount] = useState(null); // null = not loaded yet
  const {
    user,
    logout,
    isImpersonating,
    canStopImpersonation,
    impersonatingUser,
    stopImpersonation,
    totalRedeemCodes,
  } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { searchQuery, setSearchQuery, performSearch, clearSearch } =
    useSearch();
  const { newTicketsCount, showNotificationBanner, dismissBanner } =
    useNotifications();
  const { currentGradient } = useGradient();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    setIsImpersonationIndicatorDismissed(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleStopImpersonation = async () => {
    if (isStoppingImpersonation) return;

    setIsStoppingImpersonation(true);
    // Dismiss any role-scoped overlays before tearing down auth state.
    setShowLowQuotaOverlay(false);
    setShowGuidelinesModal(false);
    setShowRedeemModal(false);
    setShowPurchaseModal(false);

    try {
      const result = await stopImpersonation();

      if (result?.success === false && result.error) {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Failed to stop impersonation");
    } finally {
      setIsStoppingImpersonation(false);
    }
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;

    setSearchQuery(value);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set a new timeout for debounced search
    if (value.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 300); // 300ms debounce
    } else {
      clearSearch();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      clearSearch();
      e.target.blur();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (searchQuery.trim()) {
        performSearch(searchQuery);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdown(false);
      }
      // Close mobile menu when clicking outside
      if (
        mobileMenuOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest(".mobile-menu-toggle")
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      const landscape =
        window.innerHeight < window.innerWidth && window.innerWidth < 1024;

      setIsMobile(mobile);
      setIsLandscape(landscape);
      if (mobile || landscape) {
        setSidebarCollapsed(true);
        setMobileMenuOpen(false);
      }
      // Always close mobile menu on resize, including landscape
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    handleResize(); // Check initial size
    window.addEventListener("resize", handleResize);
    // Also handle orientation change for mobile devices
    window.addEventListener("orientationchange", () => {
      setMobileMenuOpen(false);
      handleResize();
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        const weatherData = await fetchWeatherAndSetBackground({
          setTemperature: (temp) => {
            setTemperature(temp);
          },
          setWeatherIcon: (icon) => {
            setWeatherIcon(icon);
          },
          setWeatherType: (type) => {
            setWeatherType(type);
          },
          setIsUSA: (isUS) => {
            setIsUSA(isUS);
          },
          geolocator: navigator,
        });

        let condition = "";
        const { category } = weatherData.weather;

        switch (category) {
          case "clear":
            condition = "clear";
            break;
          case "clouds":
            condition = weatherData.weather.description.includes("partly")
              ? "partly-cloudy"
              : "cloudy";
            break;
          case "rain":
            condition = "rain";
            break;
          case "snow":
            condition = "snow";
            break;
          case "mist":
            condition = "fog";
            break;
          case "thunderstorm":
            condition = "thunderstorm";
            break;
          default:
            condition = "clear";
        }

        setWeatherCondition(condition);
      } catch (error) {
        setTemperature(72);
        setWeatherIcon("fa-sun");
        setWeatherType("Clear skies");
        setIsUSA(true);
        setWeatherCondition("clear");
      }
    };

    fetchWeatherData();
    const interval = setInterval(fetchWeatherData, 600000);

    return () => clearInterval(interval);
  }, []);

  // Handle ESC key to close modals
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowRedeemModal(false);
        setShowPurchaseModal(false);
        setShowGuidelinesModal(false);
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  // Fetch earned badges count for resellers
  useEffect(() => {
    const fetchEarnedBadges = async () => {
      if (user?.type !== "Reseller" || user?.role !== "user") return;

      try {
        // Fetch playlists to calculate stats
        const { data: playlistsData } = await apiService.getPlaylists();
        const playlists = (playlistsData || []).filter(
          (playlist) => !isReadOnlyPlaylistEntry(playlist)
        );

        // Calculate stats from playlists
        const totalUsers = playlists.length;

        setUserPlaylistCount(totalUsers);
        const domains = playlists.map((p) => p.domain);
        const uniqueDomains = [...new Set(domains)].length;

        // Calculate weekly growth
        const oneWeekAgo = new Date();

        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const oneWeekAgoStr = oneWeekAgo.toISOString().split("T")[0];
        const weeklyGrowth = playlists.filter((p) => {
          const addedDate = new Date(p.addedDate || p.createdAt)
            .toISOString()
            .split("T")[0];

          return addedDate >= oneWeekAgoStr;
        }).length;

        // Fetch additional stats
        let daysWithoutChurn = 0;

        try {
          const response = await apiService.get(
            "/v1/dashboard/resellers/stats"
          );

          if (response?.data) {
            daysWithoutChurn = response.data.daysWithoutChurn || 0;
          }
        } catch (error) {
          // Silently fail
        }

        const resellerStats = {
          totalUsers,
          uniqueDomains,
          joinedDate: user?.joinDate || null,
          tierOrder: user?.resellerTierOrder || 0,
          weeklyGrowth,
          daysWithoutChurn,
        };

        const earnedBadges = getEarnedBadges(resellerStats);

        setEarnedBadgesCount(earnedBadges.length);

        // Get last seen badges from localStorage
        const lastSeenBadges = JSON.parse(
          localStorage.getItem("lastSeenBadges") || "[]"
        );

        // Find truly new badges (earned but never seen)
        const newBadges = earnedBadges.filter(
          (badge) => !lastSeenBadges.includes(badge.id)
        );

        setNewBadgesCount(newBadges.length);

        // Show splash for new badges (only if not already shown this session)
        const shownSplashBadges = JSON.parse(
          sessionStorage.getItem("shownSplashBadges") || "[]"
        );
        const unshownNewBadges = newBadges.filter(
          (badge) => !shownSplashBadges.includes(badge.id)
        );

        if (unshownNewBadges.length > 0) {
          setNewlyEarnedBadges(unshownNewBadges);
          // No splash - fireworks on sidebar badge instead

          // Mark these badges as shown in session
          sessionStorage.setItem(
            "shownSplashBadges",
            JSON.stringify([
              ...shownSplashBadges,
              ...unshownNewBadges.map((b) => b.id),
            ])
          );
        }
      } catch (error) {
        // Silently fail
      }
    };

    fetchEarnedBadges();
  }, [user]);

  // Check if we should show low quota overlay
  // - Low users (<25): Persistent until they meet 25 user requirement
  // - Low codes (≤25): Once per day reminder
  // - 0 codes: Persistent until they buy
  useEffect(() => {
    // Skip while stopping impersonation - auth state is mid-transition and
    // would briefly flash the overlay before the full page reload.
    if (isStoppingImpersonation) return;
    if (user?.type !== "Reseller" || user?.role !== "user") return;
    // Module resellers don't follow the 25-user minimum / tier ladder.
    if (user?.resellerType === "module") return;
    if (userPlaylistCount === null) return; // Wait for data to load

    // If below 25 users, always show (persistent until they meet requirement)
    if (userPlaylistCount < 25) {
      setShowLowQuotaOverlay(true);

      return;
    }

    // If 0 codes, always show (persistent until they buy)
    if (totalRedeemCodes === 0) {
      setShowLowQuotaOverlay(true);

      return;
    }

    // Low on codes (≤25) - once per day reminder
    if (totalRedeemCodes <= 25) {
      const storageKey = `lowQuotaOverlayLastShown_${user?.id || "default"}`;
      const sessionKey = `lowQuotaOverlayDismissed_${user?.id || "default"}`;
      const lastShownDate = localStorage.getItem(storageKey);
      const wasDismissedThisSession = sessionStorage.getItem(sessionKey);
      const today = new Date().toISOString().split("T")[0];

      if (lastShownDate !== today && !wasDismissedThisSession) {
        setShowLowQuotaOverlay(true);
        localStorage.setItem(storageKey, today);
      }
    }
  }, [user, totalRedeemCodes, userPlaylistCount, isStoppingImpersonation]);

  // Helper function to dismiss low quota overlay and persist in session
  const dismissLowQuotaOverlay = useCallback(() => {
    setShowLowQuotaOverlay(false);
    const sessionKey = `lowQuotaOverlayDismissed_${user?.id || "default"}`;

    sessionStorage.setItem(sessionKey, "true");
  }, [user?.id]);

  // Clear new badges count when visiting achievements page
  useEffect(() => {
    if (location.pathname === "/achievements" && earnedBadgesCount > 0) {
      // Get current earned badge IDs
      const updateSeenBadges = async () => {
        if (user?.type !== "Reseller" || user?.role !== "user") return;

        try {
          const { data: playlistsData } = await apiService.getPlaylists();
          const playlists = (playlistsData || []).filter(
            (playlist) => !isReadOnlyPlaylistEntry(playlist)
          );
          const totalUsers = playlists.length;
          const domains = playlists.map((p) => p.domain);
          const uniqueDomains = [...new Set(domains)].length;
          const oneWeekAgo = new Date();

          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          const oneWeekAgoStr = oneWeekAgo.toISOString().split("T")[0];
          const weeklyGrowth = playlists.filter((p) => {
            const addedDate = new Date(p.addedDate || p.createdAt)
              .toISOString()
              .split("T")[0];

            return addedDate >= oneWeekAgoStr;
          }).length;

          let daysWithoutChurn = 0;

          try {
            const response = await apiService.get(
              "/v1/dashboard/resellers/stats"
            );

            if (response?.data) {
              daysWithoutChurn = response.data.daysWithoutChurn || 0;
            }
          } catch (error) {
            // Silently fail
          }

          const resellerStats = {
            totalUsers,
            uniqueDomains,
            joinedDate: user?.joinDate || null,
            tierOrder: user?.resellerTierOrder || 0,
            weeklyGrowth,
            daysWithoutChurn,
          };

          const earnedBadges = getEarnedBadges(resellerStats);

          // Mark all current badges as seen
          localStorage.setItem(
            "lastSeenBadges",
            JSON.stringify(earnedBadges.map((b) => b.id))
          );
          setNewBadgesCount(0);
        } catch (error) {
          // Silently fail
        }
      };

      updateSeenBadges();
    }
  }, [location.pathname, earnedBadgesCount, user]);

  // Admin sidebar - grouped into sections. Entries marked
  // `isSectionHeader: true` render as eyebrow label + divider (no link).
  const baseAdminMenuItems = [
    { isSectionHeader: true, label: "Overview" },
    { path: "/dashboard", icon: "fas fa-tachometer-alt", label: "Dashboard" },

    { isSectionHeader: true, label: "Accounts" },
    { path: "/users", icon: "fas fa-user-shield", label: "Users" },
    { path: "/customers", icon: "fas fa-users", label: "Customers" },
    { path: "/resellers", icon: "fas fa-user-tag", label: "Resellers" },
    {
      path: "/reseller-tiers",
      icon: "fas fa-layer-group",
      label: "Reseller Tiers",
    },
    {
      path: "/invoices",
      icon: "fas fa-file-invoice-dollar",
      label: "Invoices",
    },

    { isSectionHeader: true, label: "Support" },
    {
      path: "/tickets",
      icon: "fas fa-ticket-alt",
      label: "Tickets",
      badge: newTicketsCount > 0 ? newTicketsCount.toString() : null,
    },
    {
      path: "/faq-manager",
      icon: "fas fa-question-circle",
      label: "FAQ Manager",
    },
    {
      path: "https://lunova.app/tutorials",
      icon: "fas fa-graduation-cap",
      label: "Tutorials",
      external: true,
    },
    {
      path: "/predefined-replies",
      icon: "fas fa-reply-all",
      label: "Quick Replies",
    },
    { path: "/app-messages", icon: "fas fa-bullhorn", label: "App Messages" },
    { path: "/manage-sports", icon: "fas fa-trophy", label: "Manage Sports" },

    { isSectionHeader: true, label: "Analytics" },
    { path: "/reports", icon: "fas fa-chart-bar", label: "Reports" },
    {
      path: "https://player.lunova.app/web-player/login?from=dashboard",
      icon: "fas fa-play-circle",
      label: "WebPlayer",
      external: true,
    },
    {
      path: "https://grafana.lunova.app",
      icon: "fas fa-chart-line",
      label: "Monitoring",
      external: true,
    },
  ];

  // Customer / reseller sidebar - grouped into sections. Entries marked
  // `isSectionHeader: true` render as eyebrow label + divider (no link).
  const isResellerUser = user?.type === "Reseller";
  const isModuleResellerUser =
    isResellerUser && user?.resellerType === "module";

  const customerMenuItems = [
    { isSectionHeader: true, label: "Overview" },
    { path: "/dashboard", icon: "fas fa-tv", label: "Dashboard" },

    { isSectionHeader: true, label: "Streaming" },
    {
      path: "https://player.lunova.app/web-player/login?from=dashboard",
      icon: "fas fa-play-circle",
      label: "WebPlayer",
      external: true,
    },
    {
      path: "https://lunova.app/download",
      icon: "fas fa-download",
      label: "Downloads",
      external: true,
    },
    {
      path: "#add-playlist",
      icon: "fas fa-plus-circle",
      label: "Add Playlist",
      isAction: true,
    },
  ];

  // Reseller section - visible only for resellers
  if (isResellerUser) {
    customerMenuItems.push({ isSectionHeader: true, label: "Reseller" });
    customerMenuItems.push({
      path: "/reseller-settings",
      icon: "fas fa-sliders-h",
      label: "Reseller Settings",
    });
    customerMenuItems.push({
      path: "/achievements",
      icon: "fas fa-trophy",
      label: "Achievements",
      badge: newBadgesCount > 0 ? newBadgesCount.toString() : null,
      badgeColor: "purple",
    });
    // Tier progression applies to regular resellers only; module resellers
    // don't follow the tier ladder.
    if (!isModuleResellerUser) {
      customerMenuItems.push({
        path: "/reseller-tiers",
        icon: "fas fa-layer-group",
        label: "Reseller Tiers",
      });
    }
    customerMenuItems.push({
      path: "/reseller-plans",
      icon: "fas fa-box-open",
      label: "Reseller Plans",
    });
    customerMenuItems.push({
      path: "#reseller-logs",
      icon: "fas fa-clipboard-list",
      label: "Reseller Logs",
      isAction: true,
    });
    // Module resellers get the working App Messages surface once ACTIVE;
    // regular resellers get the page as a disabled marketing preview.
    if (!isModuleResellerUser || user?.resellerStatus === "ACTIVE") {
      customerMenuItems.push({
        path: "/app-messages",
        icon: "fas fa-bullhorn",
        label: "App Messages",
      });
    }
    customerMenuItems.push({
      path: "#guidelines",
      icon: "fas fa-book-open",
      label: "Guidelines",
      isAction: true,
      highlighted: true,
    });
  }

  customerMenuItems.push(
    { isSectionHeader: true, label: "Support" },
    {
      path: "/tickets",
      icon: "fas fa-headset",
      label: "Support",
      badge: newTicketsCount > 0 ? newTicketsCount.toString() : null,
    },
    { path: "/faqs", icon: "fas fa-question-circle", label: "FAQs" },
    {
      path: "https://lunova.app/tutorials",
      icon: "fas fa-graduation-cap",
      label: "Tutorials",
      external: true,
    },

    { isSectionHeader: true, label: "Account" },
    { path: "/invoices", icon: "fas fa-credit-card", label: "Billing" },
    { path: "/profile", icon: "fas fa-user-circle", label: "My Account" }
  );

  const menuItems =
    user?.role === "admin" ? baseAdminMenuItems : customerMenuItems;

  return (
    <div
      className="min-h-screen flex transition-colors relative"
      style={{
        background: isDarkMode
          ? `linear-gradient(rgba(31, 41, 55, 0.5), rgba(31, 41, 55, 0.5)), ${currentGradient}`
          : "#fafafa",
      }}
    >
      {/* Achievement Splash Overlay */}
      <AnimatePresence>
        {showAchievementSplash && newlyEarnedBadges.length > 0 && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[100] flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => {
              setShowAchievementSplash(false);
              setNewlyEarnedBadges([]);
            }}
          >
            {/* Confetti Animation */}
            <Lottie
              animationData={confettiAnimation}
              autoplay={true}
              className="absolute inset-0 pointer-events-none"
              loop={false}
              style={{ width: "100%", height: "100%" }}
            />

            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Content */}
            <motion.div
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 rounded-2xl p-8 shadow-2xl text-center max-w-sm mx-4 pointer-events-auto"
              exit={{ opacity: 0, scale: 0.8 }}
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              transition={{ delay: 0.2, type: "spring", damping: 15 }}
            >
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {newlyEarnedBadges.length === 1
                  ? "New Badge Unlocked!"
                  : `${newlyEarnedBadges.length} New Badges Unlocked!`}
              </h2>
              <p className="text-white/80 mb-6">
                {newlyEarnedBadges.length === 1
                  ? "Congratulations on your achievement!"
                  : "Congratulations on your achievements!"}
              </p>

              {/* Badge Preview */}
              <div className="flex justify-center gap-4 mb-6 flex-wrap">
                {newlyEarnedBadges.slice(0, 3).map((badge, index) => (
                  <motion.div
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center"
                    initial={{ opacity: 0, scale: 0 }}
                    key={badge.id}
                    transition={{ delay: 0.4 + index * 0.1 }}
                  >
                    <OctagonBadge
                      badge={badge}
                      index={index}
                      isEarned={true}
                      showTooltip={false}
                      size="sm"
                    />
                    <span className="text-white/90 text-xs mt-2 font-medium">
                      {badge.name}
                    </span>
                  </motion.div>
                ))}
              </div>

              {newlyEarnedBadges.length > 3 && (
                <p className="text-white/70 text-sm mb-4">
                  +{newlyEarnedBadges.length - 3} more
                </p>
              )}

              <button
                className="px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors"
                onClick={() => {
                  setShowAchievementSplash(false);
                  setNewlyEarnedBadges([]);
                  navigate("/achievements");
                }}
              >
                View Achievements
              </button>

              <p className="text-white/50 text-xs mt-4">
                Click anywhere to dismiss
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      {(!(isMobile || isLandscape) || mobileMenuOpen) && (
        <motion.aside
          animate={{
            width: sidebarCollapsed ? 80 : 256,
            x:
              isMobile || isLandscape
                ? mobileMenuOpen
                  ? 0
                  : -(sidebarCollapsed ? 80 : 256)
                : 0,
          }}
          className={`fixed top-0 h-screen bg-white dark:bg-gray-800/95 dark:backdrop-blur-xl border-r border-gray-200 dark:border-gray-700/50 z-50 flex-shrink-0 shadow-2xl ${
            isMobile || isLandscape ? "left-0" : ""
          }`}
          initial={false}
          ref={sidebarRef}
          style={{
            minWidth: sidebarCollapsed ? 80 : 256,
            maxWidth: sidebarCollapsed ? 80 : 256,
          }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <div className="flex flex-col h-full">
            <div className="h-[73px] px-4 flex items-center border-b border-gray-200 dark:border-gray-700/50">
              {!sidebarCollapsed ? (
                <div className="flex items-center justify-between w-full">
                  <button
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-1"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      navigate("/dashboard");
                    }}
                  >
                    <img
                      alt="Logo"
                      className="w-10 h-10 rounded-xl object-contain flex-shrink-0"
                      src="/logo.png"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Cdefs%3E%3ClinearGradient id="g" x1="0%25" y1="0%25" x2="100%25" y2="100%25"%3E%3Cstop offset="0%25" style="stop-color:%23fb923c" /%3E%3Cstop offset="50%25" style="stop-color:%23fbbf24" /%3E%3Cstop offset="100%25" style="stop-color:%239333ea" /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width="100" height="100" fill="url(%23g)" rx="20" /%3E%3C/svg%3E';
                      }}
                    />
                    <motion.div
                      animate={{ opacity: 1, width: "auto" }}
                      className="text-left overflow-hidden"
                      exit={{ opacity: 0, width: 0 }}
                      initial={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                      <h1 className="text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">
                        Lunova Dash
                      </h1>
                      <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        Billing & Support
                      </p>
                    </motion.div>
                  </button>
                  {!isMobile && (
                    <button
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors p-1 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg"
                      title={
                        sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                      }
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    >
                      <i className="fas fa-bars text-lg" />
                    </button>
                  )}
                </div>
              ) : (
                <button
                  className="flex items-center justify-center w-full hover:opacity-80 transition-opacity"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    navigate("/dashboard");
                  }}
                >
                  <img
                    alt="Logo"
                    className="w-10 h-10 rounded-xl object-contain"
                    src="/logo.png"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Cdefs%3E%3ClinearGradient id="g" x1="0%25" y1="0%25" x2="100%25" y2="100%25"%3E%3Cstop offset="0%25" style="stop-color:%23fb923c" /%3E%3Cstop offset="50%25" style="stop-color:%23fbbf24" /%3E%3Cstop offset="100%25" style="stop-color:%239333ea" /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width="100" height="100" fill="url(%23g)" rx="20" /%3E%3C/svg%3E';
                    }}
                  />
                </button>
              )}
            </div>

            {sidebarCollapsed && !isMobile && (
              <div className="flex justify-center py-3">
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg"
                  title="Expand sidebar"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                >
                  <i className="fas fa-angle-right text-lg" />
                </button>
              </div>
            )}

            <nav
              className={`flex-1 ${
                sidebarCollapsed
                  ? "px-0 pt-2 overflow-y-auto"
                  : "px-4 pt-4 overflow-y-auto"
              } space-y-1 scrollbar-hide`}
            >
              {menuItems.map((item, idx) => {
                // Section header: non-interactive eyebrow + divider between
                // groups. Collapses to a thin hairline when the sidebar is
                // minimized so the visual grouping survives icon-only mode.
                if (item.isSectionHeader) {
                  if (sidebarCollapsed) {
                    return (
                      <div
                        className={`px-3 ${idx === 0 ? "pt-1 pb-2" : "pt-3 pb-2"}`}
                        key={`sec-${item.label}-${idx}`}
                      >
                        <span
                          aria-hidden="true"
                          className="block h-px bg-gray-200 dark:bg-gray-700/60"
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      className={`flex items-center gap-3 px-3 ${idx === 0 ? "pt-1 pb-2" : "pt-5 pb-2"}`}
                      key={`sec-${item.label}-${idx}`}
                    >
                      <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-gray-400 dark:text-gray-500">
                        {item.label}
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex-1 h-px bg-gray-200 dark:bg-gray-700/50"
                      />
                    </div>
                  );
                }

                // Handle action items (like Add Playlist, Guidelines)
                const handleItemClick = (e) => {
                  if (item.isAction && item.path === "#add-playlist") {
                    e.preventDefault();
                    // Navigate to dashboard first if not already there
                    if (location.pathname !== "/dashboard") {
                      navigate("/dashboard");
                    }
                    // Trigger the add playlist button with a small delay
                    setTimeout(() => {
                      const addPlaylistBtn = document.querySelector(
                        "[data-add-playlist-trigger]"
                      );

                      if (addPlaylistBtn) {
                        addPlaylistBtn.click();
                      }
                    }, 100);
                    // Close mobile menu if open
                    setMobileMenuOpen(false);
                  } else if (item.isAction && item.path === "#guidelines") {
                    e.preventDefault();
                    setShowGuidelinesModal(true);
                    setMobileMenuOpen(false);
                  } else if (item.isAction && item.path === "#reseller-logs") {
                    e.preventDefault();
                    if (isModuleResellerUser) {
                      setShowModuleAuditLogsModal(true);
                    } else {
                      setShowResellerLogsModal(true);
                    }
                    setMobileMenuOpen(false);
                  } else if (item.path === "/dashboard") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                };

                if (sidebarCollapsed) {
                  return (
                    <div className="px-3" key={item.path}>
                      <Tooltip
                        position="right"
                        text={`${item.label}${item.badge ? ` (${item.badge})` : ""}`}
                      >
                        {item.isAction ? (
                          <button
                            className={`sidebar-item justify-center w-full ${item.highlighted ? "bg-gradient-to-r from-purple-500/20 to-pink-500/20 dark:from-purple-500/30 dark:to-pink-500/30 border border-purple-500/30 dark:border-purple-500/40" : ""}`}
                            onClick={handleItemClick}
                          >
                            <div className="relative">
                              <i
                                className={`${item.icon} w-auto text-center flex-shrink-0 text-lg ${item.highlighted ? "text-purple-600 dark:text-purple-400" : ""}`}
                              />
                              {item.badge && (
                                <motion.span
                                  animate={{ scale: 1, opacity: 1 }}
                                  className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full shadow-lg shadow-red-500/50"
                                  initial={{ scale: 0, opacity: 0 }}
                                  key={item.badge}
                                  transition={{
                                    type: "spring",
                                    stiffness: 520,
                                    damping: 18,
                                  }}
                                />
                              )}
                            </div>
                          </button>
                        ) : item.external ? (
                          <a
                            className="sidebar-item justify-center"
                            href={item.path}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            <div className="relative">
                              <i
                                className={`${item.icon} w-auto text-center flex-shrink-0 text-lg`}
                              />
                              {item.badge && (
                                <motion.span
                                  animate={{ scale: 1, opacity: 1 }}
                                  className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full shadow-lg shadow-red-500/50"
                                  initial={{ scale: 0, opacity: 0 }}
                                  key={item.badge}
                                  transition={{
                                    type: "spring",
                                    stiffness: 520,
                                    damping: 18,
                                  }}
                                />
                              )}
                            </div>
                          </a>
                        ) : (
                          <NavLink
                            className={({ isActive }) =>
                              `sidebar-item ${isActive ? "sidebar-item-active" : ""} justify-center`
                            }
                            to={item.path}
                            onClick={handleItemClick}
                          >
                            <div className="relative">
                              <i
                                className={`${item.icon} w-auto text-center flex-shrink-0 text-lg`}
                              />
                              {item.badge &&
                                (item.badgeColor === "purple" ? (
                                  <motion.span
                                    animate={{
                                      scale: [1, 1.4, 1],
                                      opacity: [1, 0.8, 1],
                                    }}
                                    className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg shadow-purple-500/50"
                                    transition={{
                                      duration: 0.8,
                                      repeat: Infinity,
                                      repeatDelay: 1.5,
                                    }}
                                  />
                                ) : (
                                  <motion.span
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 shadow-lg shadow-red-500/50"
                                    initial={{ scale: 0, opacity: 0 }}
                                    key={item.badge}
                                    transition={{
                                      type: "spring",
                                      stiffness: 520,
                                      damping: 18,
                                    }}
                                  />
                                ))}
                            </div>
                          </NavLink>
                        )}
                      </Tooltip>
                    </div>
                  );
                }

                return item.isAction ? (
                  <button
                    className={`sidebar-item mx-0 w-full text-left ${item.highlighted ? "bg-gradient-to-r from-purple-500/20 to-pink-500/20 dark:from-purple-500/30 dark:to-pink-500/30 border border-purple-500/30 dark:border-purple-500/40 text-purple-700 dark:text-purple-300" : ""}`}
                    key={item.path}
                    onClick={handleItemClick}
                  >
                    <i
                      className={`${item.icon} w-5 text-center flex-shrink-0 ${item.highlighted ? "text-purple-600 dark:text-purple-400" : ""}`}
                    />
                    <motion.div
                      animate={{ opacity: 1, width: "auto" }}
                      className="flex items-center flex-1 overflow-hidden"
                      exit={{ opacity: 0, width: 0 }}
                      initial={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15, ease: "easeInOut" }}
                    >
                      <span className="flex-1 whitespace-nowrap">
                        {item.label}
                      </span>
                      {item.badge && (
                        <motion.span
                          animate={{ scale: 1, opacity: 1 }}
                          className="badge badge-danger ml-2"
                          initial={{ scale: 0.4, opacity: 0 }}
                          key={item.badge}
                          transition={{
                            type: "spring",
                            stiffness: 520,
                            damping: 18,
                          }}
                        >
                          {item.badge}
                        </motion.span>
                      )}
                    </motion.div>
                  </button>
                ) : item.external ? (
                  <a
                    className="sidebar-item mx-0"
                    href={item.path}
                    key={item.path}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <i
                      className={`${item.icon} w-5 text-center flex-shrink-0`}
                    />
                    <motion.div
                      animate={{ opacity: 1, width: "auto" }}
                      className="flex items-center flex-1 overflow-hidden"
                      exit={{ opacity: 0, width: 0 }}
                      initial={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15, ease: "easeInOut" }}
                    >
                      <span className="flex-1 whitespace-nowrap">
                        {item.label}
                      </span>
                      {item.badge && (
                        <motion.span
                          animate={{ scale: 1, opacity: 1 }}
                          className="badge badge-danger ml-2"
                          initial={{ scale: 0.4, opacity: 0 }}
                          key={item.badge}
                          transition={{
                            type: "spring",
                            stiffness: 520,
                            damping: 18,
                          }}
                        >
                          {item.badge}
                        </motion.span>
                      )}
                    </motion.div>
                  </a>
                ) : (
                  <NavLink
                    className={({ isActive }) =>
                      `sidebar-item ${isActive ? "sidebar-item-active" : ""} mx-0`
                    }
                    key={item.path}
                    to={item.path}
                    onClick={handleItemClick}
                  >
                    <i
                      className={`${item.icon} w-5 text-center flex-shrink-0`}
                    />
                    <motion.div
                      animate={{ opacity: 1, width: "auto" }}
                      className={`flex items-center flex-1 ${item.badgeColor === "purple" && item.badge ? "overflow-visible" : "overflow-hidden"}`}
                      exit={{ opacity: 0, width: 0 }}
                      initial={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.15, ease: "easeInOut" }}
                    >
                      <span className="flex-1 whitespace-nowrap overflow-hidden">
                        {item.label}
                      </span>
                      {item.badge &&
                        (item.badgeColor === "purple" ? (
                          <div className="relative ml-2 flex-shrink-0">
                            {/* Firework particles */}
                            {[...Array(6)].map((_, i) => (
                              <motion.span
                                animate={{
                                  x: [
                                    0,
                                    Math.cos((i * 60 * Math.PI) / 180) * 20,
                                  ],
                                  y: [
                                    0,
                                    Math.sin((i * 60 * Math.PI) / 180) * 20,
                                  ],
                                  opacity: [0, 1, 0],
                                  scale: [0, 1.5, 0],
                                }}
                                className="absolute w-1 h-1 rounded-full"
                                key={i}
                                style={{
                                  left: "50%",
                                  top: "50%",
                                  background: [
                                    "#FFD700",
                                    "#FF69B4",
                                    "#00FFFF",
                                    "#FF6B6B",
                                    "#9B59B6",
                                    "#F39C12",
                                  ][i],
                                }}
                                transition={{
                                  duration: 0.8,
                                  repeat: Infinity,
                                  repeatDelay: 2,
                                  delay: 0.1,
                                }}
                              />
                            ))}
                            <motion.span
                              animate={{
                                scale: [1, 1.2, 1],
                                rotate: [0, -5, 5, -5, 0],
                              }}
                              className="relative w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-pink-500 shadow-lg shadow-purple-500/50"
                              transition={{
                                duration: 0.6,
                                repeat: Infinity,
                                repeatDelay: 2,
                              }}
                            >
                              <motion.span
                                animate={{
                                  scale: [1, 1.35, 1],
                                }}
                                className="text-[10px] font-bold text-white"
                                transition={{
                                  duration: 0.5,
                                  repeat: Infinity,
                                  repeatDelay: 2.1,
                                }}
                              >
                                {item.badge}
                              </motion.span>
                            </motion.span>
                          </div>
                        ) : (
                          <motion.span
                            animate={{ scale: 1, opacity: 1 }}
                            className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full badge badge-danger"
                            initial={{ scale: 0.4, opacity: 0 }}
                            key={item.badge}
                            transition={{
                              type: "spring",
                              stiffness: 520,
                              damping: 18,
                            }}
                          >
                            {item.badge}
                          </motion.span>
                        ))}
                    </motion.div>
                  </NavLink>
                );
              })}
            </nav>

            <div
              className={`${sidebarCollapsed ? "px-0" : "px-4"} py-4 border-t border-gray-200 dark:border-gray-700/50`}
            >
              <div
                className={`flex items-center ${sidebarCollapsed ? "justify-center mx-3" : "gap-3"}`}
              >
                {sidebarCollapsed ? (
                  <Tooltip position="right" text={user?.name}>
                    <NavLink
                      className="flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
                      to="/profile"
                    >
                      {user?.avatarUrl ? (
                        <img
                          alt="Profile"
                          className="w-10 h-10 rounded-full object-cover"
                          src={user.avatarUrl}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {user?.name
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase() ||
                            user?.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </NavLink>
                  </Tooltip>
                ) : (
                  <>
                    <NavLink
                      className="flex items-center gap-3 flex-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
                      to="/profile"
                    >
                      {user?.avatarUrl ? (
                        <img
                          alt="Profile"
                          className="w-10 h-10 rounded-full object-cover"
                          src={user.avatarUrl}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {user?.name
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase() ||
                            user?.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <motion.div
                        animate={{ opacity: 1, width: "auto" }}
                        className="flex-1 overflow-hidden"
                        exit={{ opacity: 0, width: 0 }}
                        initial={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.15, ease: "easeInOut" }}
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          {user?.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {user?.role}
                        </p>
                      </motion.div>
                    </NavLink>
                    <button
                      className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors p-2"
                      title="Logout"
                      onClick={handleLogout}
                    >
                      <i className="fas fa-sign-out-alt" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.aside>
      )}

      <div
        className={`flex-1 flex flex-col w-full transition-all duration-200 ease-in-out ${
          isMobile || isLandscape ? "" : sidebarCollapsed ? "ml-20" : "ml-64"
        }`}
      >
        <main className="flex flex-col h-full w-full">
          <motion.div
            animate={{ opacity: 1 }}
            className="flex flex-col h-full w-full"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <header className="h-[73px] w-full bg-white dark:bg-gray-800/30 dark:backdrop-blur-sm border-b border-gray-200 dark:border-gray-700/50 px-4 sm:px-6 flex items-center sticky top-0 z-20">
              <div className="flex items-center justify-between w-full">
                {/* Mobile Menu Toggle */}
                <button
                  className="lg:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 mobile-menu-toggle"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <i className="fas fa-bars text-xl" />
                </button>

                <div className="flex-1 ml-3 lg:ml-0">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                    {(() => {
                      const currentPath = location.pathname;
                      const menuItem = menuItems.find(
                        (item) => item.path === currentPath
                      );

                      if (menuItem) return menuItem.label;
                      if (currentPath === "/profile") return "My Profile";
                      if (currentPath === "/faqs")
                        return "Frequently Asked Questions";
                      if (currentPath.startsWith("/customers/"))
                        return "Customer Details";
                      if (currentPath.startsWith("/tickets/"))
                        return "Ticket Detail";
                      if (currentPath.startsWith("/invoices/"))
                        return "Invoice Detail";
                      if (currentPath === "/users") return "Users";
                      if (currentPath === "/my-lunova-player")
                        return "Lunova Player";
                      if (currentPath === "/logs") return "System Monitoring";

                      return user?.role === "user"
                        ? "Dashboard"
                        : user?.name || "Dashboard";
                    })()}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 hidden sm:block">
                    {(() => {
                      const currentPath = location.pathname;
                      const isCustomer = user?.role === "user";

                      if (currentPath === "/dashboard") {
                        return isCustomer
                          ? "Manage your devices and playlists for streaming"
                          : "Drag widgets to customize your dashboard layout";
                      }
                      if (currentPath === "/devices")
                        return "Manage your streaming devices";
                      if (currentPath === "/playlists")
                        return "Manage your M3U playlists";
                      if (currentPath === "/tickets") {
                        return isCustomer
                          ? "Get help with your streaming service"
                          : "Manage customer support tickets";
                      }
                      if (currentPath === "/invoices") {
                        return isCustomer
                          ? "View your billing history and payments"
                          : "View and manage invoices";
                      }
                      if (currentPath === "/customers")
                        return "Manage customer accounts and details";
                      if (currentPath === "/resellers")
                        return "Manage reseller accounts and access";
                      if (currentPath === "/reseller-tiers")
                        return "Configure reseller tier pricing and limits";
                      if (currentPath === "/reseller-settings")
                        return "Manage the provider hosts available when adding playlists";
                      if (currentPath === "/reports")
                        return "View analytics and reports";
                      if (currentPath === "/users")
                        return "Manage system users and permissions";
                      if (currentPath === "/profile") {
                        return isCustomer
                          ? "Manage your account settings and preferences"
                          : "Manage your profile and preferences";
                      }
                      if (currentPath === "/faqs") {
                        return "Find answers to common questions";
                      }
                      if (currentPath.startsWith("/customers/"))
                        return "View and edit customer information";
                      if (currentPath.startsWith("/tickets/"))
                        return "View ticket details, add comments, and manage status";
                      if (currentPath.startsWith("/invoices/"))
                        return "View invoice details and payment history";
                      if (currentPath === "/logs")
                        return "Monitor system logs and application activity";
                      if (currentPath === "/app-messages")
                        return "Broadcast and targeted messages delivered via the client heartbeat.";

                      return `Welcome back, ${user?.name || "User"}`;
                    })()}
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  {/* Desktop Search */}
                  <div className="relative hidden lg:block">
                    <input
                      className="input pl-10 pr-4 py-2 w-64 xl:w-80 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Search customers, tickets, invoices..."
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown}
                    />
                    <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-300" />
                    {searchQuery && (
                      <button
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        onClick={clearSearch}
                      >
                        <i className="fas fa-times" />
                      </button>
                    )}
                    <SearchResults />
                  </div>

                  {/* Mobile Search Button */}
                  <button
                    className="lg:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50"
                    onClick={() => setMobileSearchOpen(true)}
                  >
                    <i className="fas fa-search text-lg" />
                  </button>

                  {/* Temperature Display */}
                  {temperature !== null && (
                    <div className="relative group hidden sm:block">
                      <div className="flex items-center gap-2 cursor-default">
                        <i
                          className={`fas ${weatherIcon || "fa-sun"} text-gray-500 dark:text-gray-400`}
                        />
                        <span
                          className="text-sm font-semibold text-gray-700 dark:text-gray-300"
                          style={{ fontFamily: "Open Sans, sans-serif" }}
                        >
                          {temperature}°{isUSA ? "F" : "C"}
                        </span>
                      </div>
                      {/* Custom tooltip */}
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                        <div className="bg-gray-800 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          {weatherType ||
                            (weatherCondition === "clear"
                              ? "Clear skies"
                              : weatherCondition === "partly-cloudy"
                                ? "Partly cloudy"
                                : weatherCondition === "cloudy"
                                  ? "Cloudy"
                                  : weatherCondition === "rain"
                                    ? "Rainy"
                                    : weatherCondition === "snow"
                                      ? "Snowing"
                                      : weatherCondition === "thunderstorm"
                                        ? "Thunderstorm"
                                        : weatherCondition === "fog"
                                          ? "Foggy"
                                          : "Current weather")}
                        </div>
                        <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
                      </div>
                    </div>
                  )}

                  <div className="relative group">
                    <button
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50"
                      onClick={toggleTheme}
                    >
                      <i
                        className={`fa-solid fa-${isDarkMode ? "sun-bright" : "moon"} text-xl`}
                      />
                    </button>
                    {/* Custom tooltip */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                      <div className="bg-gray-800 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {isDarkMode
                          ? "Switch to Light Mode"
                          : "Switch to Dark Mode"}
                      </div>
                      <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
                    </div>
                  </div>

                  {user?.role === "user" && (
                    <div className="relative group">
                      <button
                        className="p-2 text-purple-500 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
                        onClick={() => {
                          setShowRedeemModal(true);
                        }}
                      >
                        <i className="fa-solid fa-gift text-lg sm:text-xl" />
                        {totalRedeemCodes > 0 && (
                          <span className="absolute -top-1 -right-2 bg-purple-500 text-white text-xs rounded-full min-h-6 min-w-6 px-1.5 flex items-center justify-center font-semibold animate-pulse">
                            {totalRedeemCodes}
                          </span>
                        )}
                      </button>
                      {/* Custom tooltip - hidden on mobile to save space */}
                      <div className="hidden sm:block absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                        <div className="bg-gray-800 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          Redeem Code
                        </div>
                        <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
                      </div>
                    </div>
                  )}

                  <NotificationDropdown />

                  <div className="relative" ref={dropdownRef}>
                    <button
                      className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                      onClick={() => setProfileDropdown(!profileDropdown)}
                    >
                      {user?.avatarUrl ? (
                        <img
                          alt="Profile"
                          className="w-8 h-8 rounded-full object-cover"
                          src={user.avatarUrl}
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                          {user?.name
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase() || "U"}
                        </div>
                      )}
                      <i className="fas fa-chevron-down text-gray-400 dark:text-gray-500 text-xs" />
                    </button>

                    <AnimatePresence>
                      {profileDropdown && (
                        <motion.div
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          className="absolute right-0 mt-2 w-60 bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl rounded-2xl shadow-2xl ring-1 ring-gray-200/60 dark:ring-white/[0.06] overflow-hidden z-50"
                          exit={{ opacity: 0, y: -8, scale: 0.96 }}
                          initial={{ opacity: 0, y: -8, scale: 0.96 }}
                          style={{ transformOrigin: "top right" }}
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 28,
                          }}
                        >
                          <div className="p-4 border-b border-gray-200/60 dark:border-white/[0.06]">
                            <p className="text-sm font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                              {user?.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user?.email}
                            </p>
                          </div>
                          <div className="p-1.5">
                            <NavLink
                              className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors"
                              to="/profile"
                              onClick={() => setProfileDropdown(false)}
                            >
                              <i className="fas fa-user-circle w-4 text-gray-400 dark:text-gray-500" />
                              My Profile
                            </NavLink>
                            <div className="my-1.5 border-t border-gray-200/60 dark:border-white/[0.06]" />
                            <button
                              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-500/10 dark:hover:bg-red-500/15 rounded-lg transition-colors text-left"
                              onClick={handleLogout}
                            >
                              <i className="fas fa-sign-out-alt w-4" />
                              Logout
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </header>
            <section
              className={`flex-1 w-full ${location.pathname.includes("/playlist-manager/") ? "" : "p-4 lg:p-6"} overflow-y-auto bg-transparent`}
            >
              <Outlet />
            </section>
          </motion.div>
        </main>
      </div>

      {/* New Tickets Notification Banner */}
      <AnimatePresence>
        {showNotificationBanner && newTicketsCount > 0 && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            className="overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <i className="fas fa-ticket-alt text-xl animate-bounce" />
                <span className="font-medium">
                  You have {newTicketsCount} unreplied{" "}
                  {newTicketsCount === 1 ? "ticket" : "tickets"} requiring
                  attention!
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
                  onClick={() => navigate("/tickets")}
                >
                  View Tickets
                </button>
                <button
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  onClick={dismissBanner}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Search Modal */}
      <AnimatePresence>
        {mobileSearchOpen && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 z-50 lg:hidden flex flex-col"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setMobileSearchOpen(false)}
          >
            <motion.div
              animate={{ y: 0 }}
              className="bg-white dark:bg-gray-800 p-4 shadow-xl flex flex-col max-h-[85vh] overflow-hidden"
              exit={{ y: -100 }}
              initial={{ y: -100 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  onClick={() => setMobileSearchOpen(false)}
                >
                  <i className="fas fa-arrow-left" />
                </button>
                <div className="flex-1 relative">
                  <input
                    autoFocus
                    className="input pl-10 pr-4 py-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Search customers, tickets, invoices..."
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      handleSearchKeyDown(e);
                      if (e.key === "Enter" || e.key === "Escape") {
                        setMobileSearchOpen(false);
                      }
                    }}
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-300" />
                  {searchQuery && (
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      onClick={() => {
                        clearSearch();
                        setMobileSearchOpen(false);
                      }}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 flex-1 overflow-y-auto min-h-0">
                <SearchResults />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Redeem Code Modal */}
      <AnimatePresence>
        {showRedeemModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowRedeemModal(false);
              }
            }}
            onKeyDown={(e) => {
              if (shouldDismissBackdropOnKeyDown(e)) {
                e.preventDefault();
                setShowRedeemModal(false);
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800/90 dark:backdrop-blur-sm rounded-xl p-6 w-full max-w-md relative"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={() => setShowRedeemModal(false)}
              >
                <i className="fas fa-times text-lg" />
              </button>

              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-hand-holding-magic text-white text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Your Redeem Codes
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {totalRedeemCodes > 0
                    ? `Available Redeem Codes: ${totalRedeemCodes}`
                    : "No Redeem Codes Available"}
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3">
                    How Redeem Codes Work
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <li className="flex items-start gap-2">
                      <i className="fas fa-check-circle text-green-500 mt-0.5" />
                      <span>
                        Use a 30-day redeem code to activate a playlist.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fas fa-check-circle text-green-500 mt-0.5" />
                      <span>
                        Each code issues a LunaKey&trade; for fast login.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fas fa-check-circle text-green-500 mt-0.5" />
                      <span>
                        The 30-day countdown begins only when claimed.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fas fa-check-circle text-green-500 mt-0.5" />
                      <span>Codes are one-time use (unless subscribed).</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-between gap-3 mt-6">
                {/* Buy More button on the left */}
                <button
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center"
                  onClick={() => {
                    setShowRedeemModal(false);
                    setShowPurchaseModal(true);
                  }}
                >
                  <i className="fas fa-shopping-cart mr-2" />
                  Buy More
                </button>

                {/* Redeem Codes button on the right */}
                <button
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center"
                  onClick={() => {
                    setShowRedeemModal(false);
                    navigate("/dashboard");
                    // Small delay to ensure navigation completes before triggering the modal
                    setTimeout(() => {
                      const addPlaylistBtn = document.querySelector(
                        "[data-add-playlist-trigger]"
                      );

                      if (addPlaylistBtn) {
                        addPlaylistBtn.click();
                      }
                    }, 100);
                  }}
                >
                  <i className="fas fa-check mr-2" />
                  Use Redeem Codes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Purchase Modal */}
      <PurchaseModal
        show={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        onReturn={() => {
          setShowPurchaseModal(false);
          setShowRedeemModal(true);
        }}
      />

      {/* Impersonation Indicator */}
      {isImpersonating && (
        <div className="fixed bottom-4 right-4 z-[60]">
          {isImpersonationIndicatorDismissed ? (
            <button
              aria-label="Return to admin session"
              className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-yellow-400 shadow-2xl transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isStoppingImpersonation || !canStopImpersonation}
              type="button"
              onClick={handleStopImpersonation}
            >
              {isStoppingImpersonation ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2" />
                  Returning...
                </>
              ) : (
                <>
                  <i className="fas fa-user-shield mr-2" />
                  Return to Admin
                </>
              )}
            </button>
          ) : (
            <div className="relative bg-yellow-500 text-gray-900 rounded-lg shadow-2xl p-4 pr-9 max-w-sm">
              <button
                aria-label="Hide impersonation warning"
                className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-xs text-gray-900/70 transition-colors hover:bg-gray-900/10 hover:text-gray-900"
                type="button"
                onClick={() => setIsImpersonationIndicatorDismissed(true)}
              >
                <i className="fas fa-times" />
              </button>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <i className="fas fa-user-secret text-xl" />
                  <div>
                    <p className="font-semibold text-sm">Impersonating</p>
                    <p className="text-xs">
                      {impersonatingUser?.email || "User"}
                    </p>
                  </div>
                </div>
                <button
                  className="px-3 py-1.5 bg-gray-900 text-yellow-500 rounded-md hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isStoppingImpersonation || !canStopImpersonation}
                  type="button"
                  onClick={handleStopImpersonation}
                >
                  {isStoppingImpersonation ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2" />
                      Returning...
                    </>
                  ) : (
                    "Return to Admin"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ResellerLogsModal
        open={showResellerLogsModal}
        onClose={() => setShowResellerLogsModal(false)}
      />

      <ModuleAuditLogsModal
        open={showModuleAuditLogsModal}
        onClose={() => setShowModuleAuditLogsModal(false)}
      />

      {/* Partnership Guidelines Modal */}
      <AnimatePresence>
        {showGuidelinesModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowGuidelinesModal(false);
              }
            }}
            onKeyDown={(e) => {
              if (shouldDismissBackdropOnKeyDown(e)) {
                e.preventDefault();
                setShowGuidelinesModal(false);
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800/95 dark:backdrop-blur-sm rounded-2xl w-full max-w-lg relative overflow-hidden"
              exit={{ opacity: 0, scale: 0.9 }}
              initial={{ opacity: 0, scale: 0.9 }}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <i className="fas fa-handshake text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">
                        Partnership Guidelines
                      </h3>
                      <p className="text-white/80 text-sm">
                        Reseller program requirements
                      </p>
                    </div>
                  </div>
                  <button
                    className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-all bg-white/10 hover:bg-white/20 rounded-full"
                    onClick={() => setShowGuidelinesModal(false)}
                  >
                    <i className="fas fa-times text-lg" />
                  </button>
                </div>
              </div>

              {/* Guidelines content */}
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-users text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Maintain 25+ active LunaKeys
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Keep at least 25 LunaKeys claimed at all times to maintain
                      your partnership status.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-layer-group text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Purchase keys at your tier or below
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      You can only purchase keys that match your current tier
                      level or lower.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-arrows-alt-v text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Auto-upgrade/downgrade
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Your tier automatically adjusts as your customer base
                      grows or shrinks.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-exclamation-triangle text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Audits & compliance
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Audits may occur anytime. 2 strikes results in partnership
                      status loss.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 pb-6">
                <button
                  className="w-full py-3 rounded-xl bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
                  onClick={() => setShowGuidelinesModal(false)}
                >
                  Got it
                </button>
                <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4">
                  Need assistance? We're here to support your success.{" "}
                  <button
                    className="text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                    onClick={() => {
                      setShowGuidelinesModal(false);
                      navigate("/tickets");
                    }}
                  >
                    Contact Support
                  </button>
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Low Quota Overlay - Shows daily when reseller is low on credits or users */}
      <AnimatePresence>
        {showLowQuotaOverlay && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                dismissLowQuotaOverlay();
              }
            }}
            onKeyDown={(e) => {
              if (shouldDismissBackdropOnKeyDown(e)) {
                e.preventDefault();
                dismissLowQuotaOverlay();
              }
            }}
          >
            <motion.div
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="bg-white dark:bg-gray-800/95 dark:backdrop-blur-sm rounded-2xl w-full max-w-md relative overflow-hidden shadow-2xl"
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
            >
              {/* Header - changes based on severity */}
              <div
                className={`p-6 text-white bg-gradient-to-r ${
                  userPlaylistCount !== null && userPlaylistCount < 25
                    ? "from-orange-500 to-red-500"
                    : totalRedeemCodes === 0
                      ? "from-red-600 to-red-800"
                      : totalRedeemCodes <= 10
                        ? "from-red-500 to-red-600"
                        : totalRedeemCodes <= 15
                          ? "from-orange-500 to-orange-600"
                          : totalRedeemCodes <= 20
                            ? "from-yellow-500 to-yellow-600"
                            : "from-green-500 to-emerald-500"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <i
                        className={`fas ${
                          userPlaylistCount !== null && userPlaylistCount < 25
                            ? "fa-exclamation-triangle"
                            : "fa-bell"
                        } text-2xl`}
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">
                        {userPlaylistCount !== null && userPlaylistCount < 25
                          ? "Action Required"
                          : "Friendly Reminder"}
                      </h3>
                      <p className="text-white/80 text-sm">
                        {userPlaylistCount !== null && userPlaylistCount < 25
                          ? "Partnership status at risk"
                          : "Running low on LunaKeys™"}
                      </p>
                    </div>
                  </div>
                  <button
                    className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-all bg-white/10 hover:bg-white/20 rounded-full"
                    onClick={dismissLowQuotaOverlay}
                  >
                    <i className="fas fa-times text-lg" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {/* Low Credits Reminder - Color based on severity */}
                {totalRedeemCodes <= 25 && (
                  <div
                    className={`flex items-start gap-4 p-4 rounded-xl border ${
                      totalRedeemCodes === 0
                        ? "bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700/50"
                        : totalRedeemCodes <= 10
                          ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50"
                          : totalRedeemCodes <= 15
                            ? "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/50"
                            : totalRedeemCodes <= 20
                              ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/50"
                              : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        totalRedeemCodes === 0
                          ? "bg-red-200 dark:bg-red-800/50"
                          : totalRedeemCodes <= 10
                            ? "bg-red-100 dark:bg-red-900/30"
                            : totalRedeemCodes <= 15
                              ? "bg-orange-100 dark:bg-orange-900/30"
                              : totalRedeemCodes <= 20
                                ? "bg-yellow-100 dark:bg-yellow-900/30"
                                : "bg-green-100 dark:bg-green-900/30"
                      }`}
                    >
                      <i
                        className={`fas ${totalRedeemCodes === 0 ? "fa-times-circle" : "fa-coins"} ${
                          totalRedeemCodes === 0
                            ? "text-red-700 dark:text-red-300"
                            : totalRedeemCodes <= 10
                              ? "text-red-600 dark:text-red-400"
                              : totalRedeemCodes <= 15
                                ? "text-orange-600 dark:text-orange-400"
                                : totalRedeemCodes <= 20
                                  ? "text-yellow-600 dark:text-yellow-400"
                                  : "text-green-600 dark:text-green-400"
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {totalRedeemCodes === 0
                          ? "Out of LunaKeys™"
                          : totalRedeemCodes <= 10
                            ? "Very Low on LunaKeys™"
                            : totalRedeemCodes <= 15
                              ? "Low on LunaKeys™"
                              : totalRedeemCodes <= 20
                                ? "Running Low on LunaKeys™"
                                : "LunaKeys™ Reminder"}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {totalRedeemCodes === 0 ? (
                          <>
                            You have{" "}
                            <span className="font-bold text-red-700 dark:text-red-300">
                              no LunaKeys™
                            </span>{" "}
                            remaining. Purchase more now to continue adding new
                            users to your network.
                          </>
                        ) : totalRedeemCodes <= 10 ? (
                          <>
                            You only have{" "}
                            <span className="font-bold text-red-600 dark:text-red-400">
                              {totalRedeemCodes}
                            </span>{" "}
                            LunaKeys™ left! Stock up now to avoid running out.
                          </>
                        ) : (
                          <>
                            Just a heads up! You have{" "}
                            <span
                              className={`font-bold ${
                                totalRedeemCodes <= 15
                                  ? "text-orange-600 dark:text-orange-400"
                                  : totalRedeemCodes <= 20
                                    ? "text-yellow-600 dark:text-yellow-400"
                                    : "text-green-600 dark:text-green-400"
                              }`}
                            >
                              {totalRedeemCodes}
                            </span>{" "}
                            LunaKeys™ remaining. Stock up to keep adding new
                            users without interruption.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Not Enough Users Warning - Serious */}
                {userPlaylistCount !== null && userPlaylistCount < 25 && (
                  <div className="flex items-start gap-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-users text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        Below Minimum Users
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        You have{" "}
                        <span className="font-bold text-red-600 dark:text-red-400">
                          {userPlaylistCount}
                        </span>{" "}
                        of 25 required users. Add more users to maintain your
                        reseller status.
                      </p>
                    </div>
                  </div>
                )}

                {/* Warning Message - Only show removal warning for low users */}
                {userPlaylistCount !== null && userPlaylistCount < 25 ? (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <i className="fas fa-exclamation-circle text-red-500 mr-2" />
                      Failure to meet the requirements outlined in the{" "}
                      <button
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium underline underline-offset-2"
                        onClick={() => {
                          dismissLowQuotaOverlay();
                          setShowGuidelinesModal(true);
                        }}
                      >
                        Partnership Guidelines
                      </button>{" "}
                      may result in removal from the reseller program.
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <i className="fas fa-info-circle text-purple-500 mr-2" />
                      Need help? Check out the{" "}
                      <button
                        className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium underline underline-offset-2"
                        onClick={() => {
                          dismissLowQuotaOverlay();
                          setShowGuidelinesModal(true);
                        }}
                      >
                        Partnership Guidelines
                      </button>{" "}
                      for more information.
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="px-6 pb-6 space-y-3">
                {totalRedeemCodes <= 25 && (
                  <button
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg shadow-purple-500/25 flex items-center justify-center gap-2"
                    onClick={() => {
                      dismissLowQuotaOverlay();
                      setShowPurchaseModal(true);
                    }}
                  >
                    <i className="fas fa-shopping-cart" />
                    Buy More LunaKeys
                  </button>
                )}

                {userPlaylistCount !== null && userPlaylistCount < 25 && (
                  <button
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                    onClick={() => {
                      dismissLowQuotaOverlay();
                      navigate("/dashboard?open=add-playlist");
                    }}
                  >
                    <i className="fas fa-user-plus" />
                    Add New User
                  </button>
                )}

                <button
                  className="w-full py-3 rounded-xl bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
                  onClick={dismissLowQuotaOverlay}
                >
                  Remind Me Tomorrow
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
